import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { Checkbox, IconButton, MenuItem, Select } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ImportExcel from '../../Components/ImportExcel';
import ExportExcel from '../../Components/ExportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  renderTextEditor,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';

const cashflowgroup = [
  'Cash flow from Operating Activities',
  'Cash flow from Investing Activities',
  'Cash flow from Financing Activities',
];

const initialFilters = {
  AccountNumber: '',
  AccountName: '',
  AccountGLType: 'All',
  AccountType: 'All',
  AccountSubType: 'All',
  CashFlowGroup: 'All',
  CashFlowCategory: 'All',
  DimCompanyKey: 'All',
  DimOrganisationKey: 'All',
  enabled: false,
};

const getCompanyNameById = (companies, id) =>
  companies?.find((company) => company.id === id)?.CompanyName || '';

const getOrganisationNameById = (organisations, id) =>
  organisations?.find((org) => org.id === id)?.OrgName || '';

function ColumnHeader({ label, colKey, frozenColumns, sortColumns, onFreeze }) {
  const isFrozen = frozenColumns.includes(colKey);
  const sort = sortColumns.find((c) => c.columnKey === colKey);
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <b style={{ marginRight: 5 }}>{label}</b>
      <VpnKeyIcon
        onClick={(e) => onFreeze(e, colKey)}
        style={{ fontSize: 20, opacity: isFrozen ? 1 : 0.2 }}
      />
      {sort?.direction === 'ASC' ? (
        <ArrowDropUpIcon />
      ) : sort?.direction === 'DESC' ? (
        <ArrowDropDownIcon />
      ) : null}
    </div>
  );
}

function GeneralLedgerTable({
  name,
  rows,
  companies,
  organisations,
  activeFilter,
  orgLength,
  selectedDates,
  getStateData,
  onClickedAdd,
  onClickedDelete,
  resetReportingState,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [cashflowcategory, setCashflowcategory] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [accountSubTypes, setAccountSubTypes] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);
  
  useEffect(() => {
    const asyncFn = async () => {
      await Promise.all([
        ManageReportDataApi.getTemplate('CashFlowCategory')
          .then((res) => {
            if (res.status === 200) {
              setCashflowcategory(
                res.data
                  ? res.data.sort((a, b) =>
                      a.CashflowCategoryCode < b.CashflowCategoryCode ? -1 : 1,
                    )
                  : [],
              );
            } else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getTemplate('AccountType')
          .then((res) => {
            if (res.status === 200) {
              setAccountTypes(
                res.data
                  ? res.data.sort((a, b) =>
                      a.AccountTypeCode < b.AccountTypeCode ? -1 : 1,
                    )
                  : [],
              );
            } else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message)),
        ManageReportDataApi.getTemplate('AccountSubType')
          .then((res) => {
            if (res.status === 200) {
              setAccountSubTypes(
                res.data
                  ? res.data.sort((a, b) =>
                      a.AccountSubTypeCode < b.AccountSubTypeCode ? -1 : 1,
                    )
                  : [],
              );
            } else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message)),
      ]);
    };
    asyncFn();
    return () => setTableRows([]);
  }, []);

  useEffect(() => {
    setTableRows(activeFilter ? rows.filter((row) => row.IsActive) : rows);
  }, [rows, activeFilter]);

  const handleClickRow = useCallback((ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  }, []);

  const handleCellClick = useCallback((rowData) => {
    setSelectedRow(rowData.row);
  }, []);

  const toggleFilters = useCallback(() => {
    setFilters((current) =>
      current.enabled
        ? { ...initialFilters }
        : { ...current, enabled: true },
    );
  }, []);

  const handleClickDelete = useCallback(() => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else if (selectedRow) {
      onClickedDelete(name, [selectedRow.id]);
      setSelectedRow(null);
    }
  }, [selectedIds, selectedRow, name, onClickedDelete]);

  const handleGridRowsUpdated = useCallback((updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    if (field.column.key === 'AccountNumber' && isNaN(updated_data.updated[field.column.key])) {
      toast.warning('Please insert the number value.');
      return;
    }
    
    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];
    }

    const updatedValue = updatedRows[field.indexes[0]][field.column.key];

    // updatedRows is the filtered/sorted grid view. Replacing tableRows with it
    // permanently discarded rows hidden by a search, so clearing the search could
    // not restore the full ledger. Merge the edited value into the full list by id.
    setTableRows((currentRows) =>
      currentRows.map((row) =>
        updated_data.rowIds.includes(row.id)
          ? { ...row, [field.column.key]: updatedValue }
          : row,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) {
          toast.error(res.message);
        } else {
          resetReportingState();
        }
      })
      .catch((error) => toast.error(error.message));
  }, [name, selectedIds, resetReportingState]);

  const handleSelectFrozenColumns = useCallback((event, colName) => {
    event.stopPropagation();
    event.preventDefault();
    setFrozenColumns((prev) => {
      const idx = prev.indexOf(colName);
      if (idx === -1) return [...prev, colName];
      const newCols = [...prev];
      newCols.splice(idx, 1);
      return newCols;
    });
  }, []);

  const handleExcelImport = (updated_data) => {
    // check import data is valid
    const validData = updated_data.every(item => {
      return item.hasOwnProperty('AccountNumber') && item.hasOwnProperty('AccountName') && item.hasOwnProperty('AccountGLType')
        && item.hasOwnProperty('AccountType') && item.hasOwnProperty('AccountSubType') && item.hasOwnProperty('CashFlowGroup')
        && item.hasOwnProperty('CashFlowCategory') && item.hasOwnProperty('Income');
    });
    if (!validData) {
      toast.warning('Please insert the valid data. Data must contain AccountNumber, AccountName, AccountGLType, AccountType, AccountSubType, CashFlowGroup, CashFlowCategory, and Income.');
      return;
    }

    ManageReportDataApi.importDataFromExcel(
      name,
      updated_data,
      selectedDates,
    ).then((res) => {
      if (res.status === 200) {
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const generalLedgerColumns = useMemo(() => {
    const header = (label, key) => (
      <ColumnHeader
        label={label}
        colKey={key}
        frozenColumns={frozenColumns}
        sortColumns={sortColumns}
        onFreeze={handleSelectFrozenColumns}
      />
    );
    let cols = [
      SelectColumn,
      {
        key: 'AccountNumber',
        name: header('Account Number', 'AccountNumber'),
        width: 155,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.AccountNumber}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, AccountNumber: e.target.value }))
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.includes('AccountNumber'),
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.AccountNumber}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                AccountNumber: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'AccountName',
        name: header('Account Name', 'AccountName'),
        width: 175,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.AccountName}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, AccountName: e.target.value }))
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.includes('AccountName'),
        renderEditCell: renderTextEditor,
      },
      {
        key: 'AccountGLType',
        name: header('Account GL Type', 'AccountGLType'),
        width: 160,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.AccountGLType}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, AccountGLType: e.target.value }))
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                <option value="BS">BS</option>
                <option value="P&L">P&L</option>
              </select>
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.includes('AccountGLType'),
        renderCell: (props) => (
          <Select
            fullWidth
            variant="standard"
            value={props.row.AccountGLType || ''}
            onChange={(e) =>
              props.onRowChange({
                ...props.row,
                AccountGLType: e.target.value,
              }, true)
            }
          >
            <MenuItem value={null}>None</MenuItem>
            <MenuItem value="BS">BS</MenuItem>
            <MenuItem value="P&L">P&L</MenuItem>
          </Select>
        ),
      },
      {
        key: 'AccountType',
        name: header('Account Type', 'AccountType'),
        width: 175,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.AccountType}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, AccountType: e.target.value }))
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {accountTypes.map((cur, index) => (
                  <option key={index} value={cur.AccountTypeCode}>
                    {cur.AccountTypeCode}
                  </option>
                ))}
              </select>
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.includes('AccountType'),
        renderCell: (props) => (
          <Select
            fullWidth
            variant="standard"
            value={props.row.AccountType || ''}
            onChange={(e) =>
              props.onRowChange({
                ...props.row,
                AccountType: e.target.value,
              }, true)
            }
          >
            <MenuItem value={null}>None</MenuItem>
            {accountTypes.map((cur, index) => (
              <MenuItem key={index} value={cur.AccountTypeCode}>
                {cur.AccountTypeCode}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'AccountSubType',
        name: header('Account SubType', 'AccountSubType'),
        width: 160,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.AccountSubType}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, AccountSubType: e.target.value }))
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {accountSubTypes.map((cur, index) => (
                  <option key={index} value={cur.AccountSubTypeCode}>
                    {cur.AccountSubTypeCode}
                  </option>
                ))}
              </select>
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.includes('AccountSubType'),
        renderCell: (props) => (
          <Select
            fullWidth
            variant="standard"
            value={props.row.AccountSubType || ''}
            onChange={(e) =>
              props.onRowChange({
                ...props.row,
                AccountSubType: e.target.value,
              }, true)
            }
          >
            <MenuItem value={null}>None</MenuItem>
            {accountSubTypes.map((cur, index) => (
              <MenuItem key={index} value={cur.AccountSubTypeCode}>
                {cur.AccountSubTypeCode}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'CashFlowGroup',
        name: header('Cash Flow Group', 'CashFlowGroup'),
        width: 160,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.CashFlowGroup}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, CashFlowGroup: e.target.value }))
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {cashflowgroup.map((cur, index) => (
                  <option key={index} value={cur}>
                    {cur}
                  </option>
                ))}
              </select>
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.includes('CashFlowGroup'),
        renderCell: (props) => (
          <Select
            fullWidth
            variant="standard"
            value={props.row.CashFlowGroup || ''}
            onChange={(e) =>
              props.onRowChange({
                ...props.row,
                CashFlowGroup: e.target.value,
              }, true)
            }
          >
            <MenuItem value={null}>None</MenuItem>
            {cashflowgroup.map((cur, index) => (
              <MenuItem key={index} value={cur}>
                {cur}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'CashFlowCategory',
        name: header('Cash Flow Category', 'CashFlowCategory'),
        width: 205,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.CashFlowCategory}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, CashFlowCategory: e.target.value }))
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {cashflowcategory.map((cur, index) => (
                  <option
                    key={index}
                    value={cur.CashflowCategoryCode || ''}
                  >
                    {cur.CashflowCategoryCode}
                  </option>
                ))}
              </select>
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.includes('CashFlowCategory'),
        renderCell: (props) => (
          <Select
            fullWidth
            variant="standard"
            value={props.row.CashFlowCategory || ''}
            onChange={(e) =>
              props.onRowChange({
                ...props.row,
                CashFlowCategory: e.target.value,
              }, true)
            }
          >
            <MenuItem value={null}>None</MenuItem>
            {cashflowcategory.map((cur, index) => (
              <MenuItem
                key={index}
                value={cur.CashflowCategoryCode || ''}
              >
                {cur.CashflowCategoryCode}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'Income',
        name: header('Income', 'Income'),
        frozen: frozenColumns.includes('Income'),
        renderCell: (props) => (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              checked={props.row.Income === true}
              onChange={(e) => {
                props.onRowChange({
                  ...props.row,
                  Income: e.target.checked,
                }, true);
              }}
              color="success"
            />
          </div>
        ),
      },
      {
        key: 'IsActive',
        name: header('Active', 'IsActive'),
        frozen: frozenColumns.includes('IsActive'),
        renderCell: (props) => (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              checked={props.row.IsActive}
              onChange={(e) => {
                props.onRowChange({
                  ...props.row,
                  IsActive: e.target.checked,
                }, true);
              }}
              color="success"
            />
          </div>
        ),
      },
    ];

    // Add organization/company columns if needed
    if (orgLength.length > 1) {
      cols.splice(1, 0, {
        key: 'DimOrganisationKey',
        name: header('Department', 'DimOrganisationKey'),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.includes('DimOrganisationKey'),
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, DimOrganisationKey: e.target.value }))
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => (
          <Select
            fullWidth
            variant="standard"
            value={props.row.DimOrganisationKey || ''}
            onChange={(e) =>
              props.onRowChange(
                { ...props.row, DimOrganisationKey: e.target.value },
                true,
              )
            }
          >
            <MenuItem value={null}>None</MenuItem>
            {organisations &&
              organisations.map(
                (cur) =>
                  props.row.DimCompanyKey === cur.DimCompanyKey &&
                  cur.IsActive && (
                    <MenuItem key={cur.id} value={cur.id}>
                      {cur.OrgName}
                    </MenuItem>
                  ),
              )}
          </Select>
        ),
      });
    }

    if (companies.length > 1) {
      cols.splice(1, 0, {
        key: 'DimCompanyKey',
        name: header('Company', 'DimCompanyKey'),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.includes('DimCompanyKey'),
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, DimCompanyKey: e.target.value }))
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => (
          <Select
            fullWidth
            variant="standard"
            value={props.row.DimCompanyKey || ''}
            onChange={(e) =>
              props.onRowChange(
                { ...props.row, DimCompanyKey: e.target.value },
                true,
              )
            }
          >
            <MenuItem value={null}>None</MenuItem>
            {companies &&
              companies.map(
                (cur) =>
                  cur.IsActive && (
                    <MenuItem key={cur.id} value={cur.id}>
                      {cur.CompanyName}
                    </MenuItem>
                  ),
              )}
          </Select>
        ),
      });
    }

    // Ensure frozen columns are at the start
    let frozenCols = cols.filter((col) => col.frozen === true);
    let unfrozenCols = cols.filter((col) => col.frozen !== true);
    return [...frozenCols, ...unfrozenCols];
  }, [
    tableRows,
    frozenColumns,
    sortColumns,
    handleSelectFrozenColumns,
    accountTypes,
    accountSubTypes,
    cashflowcategory,
    companies,
    organisations,
    orgLength    
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.AccountName
          ? r.AccountName?.toLowerCase().includes(filters.AccountName.toLowerCase())
          : true) &&
        (filters.AccountNumber
          ? r.AccountNumber?.toString().includes(filters.AccountNumber)
          : true) &&
        (filters.AccountGLType !== 'All'
          ? r.AccountGLType === filters.AccountGLType
          : true) &&
        (filters.AccountType !== 'All'
          ? r.AccountType === filters.AccountType
          : true) &&
        (filters.AccountSubType !== 'All'
          ? r.AccountSubType === filters.AccountSubType
          : true) &&
        (filters.CashFlowGroup !== 'All'
          ? r.CashFlowGroup === filters.CashFlowGroup
          : true) &&
        (filters.CashFlowCategory !== 'All'
          ? r.CashFlowCategory === filters.CashFlowCategory
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true)
      );
    });
  }, [tableRows, filters]);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'AccountNumber':
        return (a, b) => a[sortColumn] - b[sortColumn];
      case 'AccountName':
      case 'AccountGLType':
      case 'AccountType':
      case 'AccountSubType':
      case 'CashFlowGroup':
      case 'CashFlowCategory':
        return (a, b) => (a[sortColumn] || '').localeCompare(b[sortColumn] || '');
      case 'DimCompanyKey':
        return (a, b) => getCompanyNameById(companies, a[sortColumn]).localeCompare(getCompanyNameById(companies, b[sortColumn]));
      case 'DimOrganisationKey':
        return (a, b) => getOrganisationNameById(organisations, a[sortColumn]).localeCompare(getOrganisationNameById(organisations, b[sortColumn]));
      default:
        return () => 0;
    }
  };

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns, companies, organisations]);

  const exportValue = useMemo(() => {
    let export_value = {
      GeneralLedger: {
        columns: [],
        data: [],
      },
      Company: {
        columns: [],
        data: [],
      },
      Organisation: {
        columns: [],
        data: [],
      },
    };

    export_value.GeneralLedger.columns = [
      { title: 'DimCompanyKey', width: { wpx: 80 } },
      { title: 'DimOrganisationKey', width: { wpx: 80 } },
      { title: 'AccountNumber', width: { wpx: 80 } },
      { title: 'AccountName', width: { wpx: 80 } },
      { title: 'AccountGLType', width: { wpx: 80 } },
      { title: 'AccountType', width: { wpx: 80 } },
      { title: 'AccountSubType', width: { wpx: 80 } },
      { title: 'CashFlowGroup', width: { wpx: 80 } },
      { title: 'CashFlowCategory', width: { wpx: 80 } },
      { title: 'Income', width: { wpx: 80 } },
      { title: 'IsActive', width: { wpx: 80 } },
    ];
    tableRows.forEach((data) => {
      export_value.GeneralLedger.data.push([
        {
          value: data.DimCompanyKey ? Number(data.DimCompanyKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimOrganisationKey ? Number(data.DimOrganisationKey) : -1,
          style: { numFmt: '0' },
        },
        { value: data.AccountNumber ? data.AccountNumber : '' },
        { value: data.AccountName ? data.AccountName : '' },
        { value: data.AccountGLType ? data.AccountGLType : '' },
        { value: data.AccountType ? data.AccountType : '' },
        { value: data.AccountSubType ? data.AccountSubType : '' },
        { value: data.CashFlowGroup ? data.CashFlowGroup : '' },
        { value: data.CashFlowCategory ? data.CashFlowCategory : '' },
        { value: data.Income ? 'true' : 'false' },
        { value: data.IsActive ? 'true' : 'false' },
      ]);
    });

    export_value.Company.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'CompanyName', width: { wpx: 80 } },
    ];
    companies &&
      companies.forEach((cur) => {
        export_value.Company.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.CompanyName },
        ]);
      });

    export_value.Organisation.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'OrgName', width: { wpx: 80 } },
    ];
    organisations &&
      organisations.forEach((cur) => {
        export_value.Organisation.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.OrgName },
        ]);
      });

    return export_value;
  }, [tableRows, companies, organisations]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} />
        <ExportExcel filename="GeneralLedger" value={exportValue} />
        <IconButton aria-label="Add" onClick={() => onClickedAdd(name)}>
          <AddIcon />
        </IconButton>
        <IconButton aria-label="Search" onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={generalLedgerColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>
    </div>
  );
}

GeneralLedgerTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  selectedDates: PropTypes.array.isRequired,
  orgLength: PropTypes.array.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
};

export default GeneralLedgerTable;
